import { useCallback, useRef, useState } from "react";

/**
 * Verschiebbarer Spalten-Teiler für die Detail-Ansicht (Scan | Tabelle).
 *
 * Anlassfall 2026-06-15 (Birgit): das starre 50/50-Grid lässt sich nicht an
 * die Bildschirmbreite anpassen — die Tabelle läuft auf Laptop-Breiten über
 * ihren 50%-Pane, und „die linke Seite verkleinern" hat nichts gebracht, weil
 * der Scan-Zoom das Grid nicht rebalanciert. Dieser Hook steuert NUR
 * `grid-template-columns` des Containers (die Panes selbst bleiben unberührt —
 * kein Eingriff in overflow/sticky von PdfWithHighlights) und merkt sich die
 * gewählte Breite pro Browser (localStorage).
 *
 * Rückgabe:
 *   bodyRef   → ref auf den Grid-Container (zum Messen der Breite beim Ziehen)
 *   pct       → aktuelle Breite des linken (Scan-)Panes in % (25..75)
 *   startDrag → onPointerDown-Handler für den Teiler
 */
export function useColumnSplit(storageKey: string, defaultPct = 45) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const read = (): number => {
    try {
      const v = Number(localStorage.getItem(storageKey));
      return v >= 25 && v <= 75 ? v : defaultPct;
    } catch {
      return defaultPct;
    }
  };
  const [pct, setPct] = useState<number>(read);
  const pctRef = useRef<number>(pct);
  pctRef.current = pct;

  const startDrag = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    const move = (ev: PointerEvent) => {
      const el = bodyRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const p = Math.max(25, Math.min(75, ((ev.clientX - r.left) / r.width) * 100));
      pctRef.current = p;
      setPct(p);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      try { localStorage.setItem(storageKey, String(Math.round(pctRef.current))); } catch { /* ignore */ }
    };
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }, [storageKey]);

  return { bodyRef, pct, startDrag };
}
